import { useEffect, useState } from "react";
import client from "@/lib/glossyApi";
import { toast } from "sonner";
import { Plus, Trash2, Instagram, BellRing, Clock, X } from "lucide-react";
import { Header, Field, Area, Select, Drawer } from "./shared";

const STAGES = [
  { value: "new", label: "New" },
  { value: "contacted", label: "Contacted" },
  { value: "interested", label: "Interested" },
  { value: "payment_sent", label: "Payment sent" },
  { value: "member", label: "Member ✓" },
  { value: "not_now", label: "Not now" },
];
const STAGE_LABEL = Object.fromEntries(STAGES.map((s) => [s.value, s.label]));
const SOURCES = ["Instagram DM", "Instagram comment", "Email", "Referral", "Other"];
const STAGE_STYLE = {
  new: "border border-ink/40 text-ink/70",
  contacted: "border border-gloss-silver text-ink",
  interested: "bg-gloss-gold text-ink",
  payment_sent: "bg-ink/80 text-cream",
  member: "bg-ink text-cream",
  not_now: "border border-ink/25 text-ink/40",
};

const blankLead = { name: "", ig_handle: "", business_name: "", source: "Instagram DM", stage: "new", notes: "", next_follow_up: "" };

function BookingsPanel() {
  const [bookings, setBookings] = useState([]);
  const [meet, setMeet] = useState("");
  const [meetSaved, setMeetSaved] = useState("");
  const load = () => client.get("/admin/bookings").then((r) => setBookings(r.data)).catch(() => {});
  useEffect(() => {
    load();
    client.get("/admin/settings").then((r) => { setMeet(r.data.meet_link || ""); setMeetSaved(r.data.meet_link || ""); }).catch(() => {});
  }, []);
  const saveMeet = async () => {
    try {
      await client.patch("/admin/settings", { meet_link: meet.trim() });
      setMeetSaved(meet.trim());
      toast.success("Video link saved — it goes into every video-call confirmation.");
    } catch { toast.error("Could not save the link."); }
  };
  const del = async (b) => {
    if (!window.confirm(`Delete ${b.name}'s booking on ${b.date} at ${b.time}? The slot opens up again.`)) return;
    try { await client.delete(`/admin/bookings/${b.id}`); toast.success("Booking deleted — slot freed."); load(); }
    catch { toast.error("Could not delete that booking."); }
  };
  return (
    <div className="mx-6 sm:mx-10 mt-6 border border-ink/20 bg-white p-5 sm:p-6" data-testid="bookings-panel">
      <p className="overline text-ink mb-1">Booked calls — {bookings.length}</p>
      <p className="font-sans text-xs text-ink/60 mb-3">Deleting a booking frees its time slot on the calendar.</p>
      <div className="flex gap-2 mb-4 flex-wrap items-center">
        <input value={meet} onChange={(e) => setMeet(e.target.value)} placeholder="Video calls auto-create a unique room per booking — or paste one fixed link (e.g. Google Meet) to always use it"
          className="flex-1 min-w-64 bg-cream border border-ink/20 px-3 py-2 font-mono text-xs focus:outline-none focus:border-gloss-gold" data-testid="meet-link-input" />
        <button onClick={saveMeet} disabled={meet.trim() === meetSaved}
          className="overline text-[10px] px-3 py-2.5 bg-ink text-cream hover:bg-gloss-gold hover:text-ink transition-colors disabled:opacity-40" data-testid="meet-link-save">Save link</button>
      </div>
      {bookings.length === 0 && <p className="font-sans text-sm text-ink/50">No calls booked yet.</p>}
      <div className="space-y-2">
        {bookings.map((b) => (
          <div key={b.id} className="flex items-center gap-3 border border-ink/10 px-3 py-2.5 flex-wrap" data-testid={`booking-${b.id}`}>
            <span className="font-mono text-[11px] text-ink font-bold whitespace-nowrap">{b.date} · {b.time}</span>
            <span className={`overline text-[9px] px-2 py-0.5 ${b.call_type === "video" ? "bg-gloss-gold/25 text-ink" : "bg-ink/10 text-ink/70"}`}>{b.call_type === "video" ? "Video" : "Phone"}</span>
            <span className="font-sans text-sm text-ink truncate">{b.name}{b.business_name ? ` · ${b.business_name}` : ""}</span>
            <span className="font-mono text-[10px] text-ink/50 truncate">{b.email}{b.phone ? ` · ${b.phone}` : ""}</span>
            {b.video_link && (
              <a href={b.video_link} target="_blank" rel="noreferrer" className="font-mono text-[10px] text-gloss-gold underline truncate max-w-48" data-testid={`booking-link-${b.id}`}>Join room ↗</a>
            )}
            <button onClick={() => del(b)} className="ml-auto overline text-[9px] px-2.5 py-1.5 border border-red-300 text-red-600 hover:bg-red-600 hover:text-white transition-colors" data-testid={`booking-delete-${b.id}`}>Delete</button>
          </div>
        ))}
      </div>
    </div>
  );
}

export default function Leads() {
  const [leads, setLeads] = useState([]);
  const [briefing, setBriefing] = useState(null);
  const [filter, setFilter] = useState("");
  const [drawer, setDrawer] = useState(null); // null | {lead object being edited or blank}
  const [saving, setSaving] = useState(false);
  const [dmHandle, setDmHandle] = useState("");
  const [dmAbout, setDmAbout] = useState("");
  const [dmText, setDmText] = useState("");
  const [dmBusy, setDmBusy] = useState(false);

  const draftDm = async () => {
    if (!dmHandle.trim()) { toast.error("Add their Instagram handle first."); return; }
    setDmBusy(true);
    try {
      const r = await client.post("/admin/leads/draft-dm", { ig_handle: dmHandle, about: dmAbout });
      setDmText(r.data.dm);
      toast.success("DM drafted — lead saved below.");
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Drafting failed — try again."); }
    setDmBusy(false);
  };

  const load = async () => {
    const [l, b] = await Promise.all([client.get("/admin/leads"), client.get("/admin/leads/briefing")]);
    setLeads(l.data);
    setBriefing(b.data);
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    if (!drawer.name.trim() && !drawer.ig_handle.trim()) { toast.error("Give them a name or an Instagram handle"); return; }
    setSaving(true);
    try {
      if (drawer.id) {
        const { id, created_at, last_touch, ...body } = drawer;
        await client.patch(`/admin/leads/${id}`, body);
        toast.success("Lead updated");
      } else {
        await client.post("/admin/leads", drawer);
        toast.success("Lead added to the book");
      }
      setDrawer(null);
      await load();
    } catch { toast.error("Could not save the lead"); }
    setSaving(false);
  };

  const setStage = async (lead, stage) => {
    await client.patch(`/admin/leads/${lead.id}`, { stage });
    if (stage === "member") toast.success(`${lead.name || "@" + lead.ig_handle} is now a member 🎉`);
    await load();
  };

  const snooze = async (lead, days) => {
    const d = new Date(); d.setDate(d.getDate() + days);
    await client.patch(`/admin/leads/${lead.id}`, { next_follow_up: d.toISOString().slice(0, 10) });
    toast.success(`Follow-up moved ${days} day${days > 1 ? "s" : ""} out`);
    await load();
  };

  const remove = async (lead) => {
    await client.delete(`/admin/leads/${lead.id}`);
    toast.success("Lead removed");
    await load();
  };

  const shown = filter ? leads.filter((l) => l.stage === filter) : leads;
  const counts = briefing?.counts || {};
  const active = leads.filter((l) => !["member", "not_now"].includes(l.stage)).length;

  return (
    <div data-testid="leads-page">
      <div className="mx-6 sm:mx-10 mt-6 border border-ink/15 p-5 sm:p-6" data-testid="outreach-panel">
        <p className="overline text-gloss-silver">Instagram outreach — DM drafter</p>
        <p className="font-sans text-xs text-ink/55 mt-1 mb-3">Found a business worth approaching? Drop in their handle and what they do — you get a personal DM to copy-paste and a lead card to track them. Send by hand, 15–20 a day keeps your account safe.</p>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <input value={dmHandle} onChange={(e) => setDmHandle(e.target.value)} placeholder="@their-handle"
            className="bg-white border border-ink/25 px-3 py-2.5 font-sans text-sm focus:outline-none focus:border-gloss-gold" data-testid="outreach-handle" />
          <input value={dmAbout} onChange={(e) => setDmAbout(e.target.value)} placeholder="What they do (e.g. florist in Leeds, beautiful bouquets)"
            className="bg-white border border-ink/25 px-3 py-2.5 font-sans text-sm focus:outline-none focus:border-gloss-gold sm:col-span-2" data-testid="outreach-about" />
        </div>
        <button onClick={draftDm} disabled={dmBusy} className="mt-3 overline text-[11px] px-4 py-2.5 bg-ink text-cream hover:bg-gloss-gold hover:text-ink transition-colors disabled:opacity-50" data-testid="outreach-draft-btn">
          {dmBusy ? "Writing…" : "Draft my DM (~1p)"}
        </button>
        {dmText && (
          <div className="mt-4 border border-gloss-gold/50 bg-gloss-gold/10 p-4" data-testid="outreach-result">
            <p className="font-sans text-sm whitespace-pre-wrap">{dmText}</p>
            <button onClick={() => { navigator.clipboard.writeText(dmText); toast.success("DM copied — paste it into Instagram."); }}
              className="mt-3 overline text-[10px] px-3 py-2 border border-ink/30 hover:bg-ink hover:text-cream transition-colors" data-testid="outreach-copy-btn">
              Copy DM
            </button>
          </div>
        )}
      </div>
      <Header kicker="Editorial Assistant" title="Leads">
        <button onClick={() => setDrawer({ ...blankLead })} className="overline bg-ink text-cream px-5 py-3 hover:bg-ink/85 transition-colors flex items-center gap-2" data-testid="add-lead-btn">
          <Plus size={14} /> Add a lead
        </button>
      </Header>

      <BookingsPanel />

      {briefing && (briefing.due.length > 0 || briefing.stale.length > 0) && (
        <div className="mx-6 sm:mx-10 mt-6 border border-gloss-gold bg-gloss-gold/10 p-5 sm:p-6" data-testid="leads-briefing">
          <p className="overline text-ink flex items-center gap-2 mb-3"><BellRing size={14} /> Today's briefing</p>
          <div className="space-y-2">
            {briefing.due.map((l) => (
              <BriefRow key={l.id} l={l} note={`Follow-up due ${l.next_follow_up === briefing.today ? "today" : l.next_follow_up}`} onSnooze={() => snooze(l, 2)} onDone={() => setStage(l, "contacted")} />
            ))}
            {briefing.stale.map((l) => (
              <BriefRow key={l.id} l={l} note="Gone quiet — no touch in 5+ days" onSnooze={() => snooze(l, 2)} onDone={() => setStage(l, "contacted")} />
            ))}
          </div>
        </div>
      )}

      <div className="px-6 sm:px-10 py-6 flex flex-wrap gap-2" data-testid="leads-stage-filters">
        <FilterChip label={`All active · ${active}`} on={() => setFilter("")} selected={filter === ""} testid="filter-all" />
        {STAGES.map((s) => (
          <FilterChip key={s.value} label={`${s.label} · ${counts[s.value] ?? 0}`} on={() => setFilter(s.value)} selected={filter === s.value} testid={`filter-${s.value}`} />
        ))}
      </div>

      <div className="px-6 sm:px-10 pb-12">
        {shown.length === 0 ? (
          <div className="border border-ink/15 bg-white p-10 text-center" data-testid="leads-empty">
            <p className="font-serif text-2xl mb-2">The book is empty{filter ? " at this stage" : ""}.</p>
            <p className="font-sans text-sm text-ink/55">When someone DMs or comments “FEATURE ME”, add them here so nobody slips through.</p>
          </div>
        ) : (
          <div className="border border-ink/15 divide-y divide-ink/10 bg-white" data-testid="leads-list">
            {shown.map((l) => (
              <div key={l.id} className="px-5 py-4 flex flex-col sm:flex-row sm:items-center gap-3" data-testid={`lead-row-${l.id}`}>
                <div className="min-w-0 flex-1 cursor-pointer" onClick={() => setDrawer({ ...blankLead, ...l })}>
                  <p className="font-serif text-lg truncate">{l.name || `@${l.ig_handle}`}{l.business_name ? <span className="text-ink/50"> — {l.business_name}</span> : null}</p>
                  <p className="font-sans text-xs text-ink/55 truncate">
                    {l.ig_handle ? `@${l.ig_handle} · ` : ""}{l.email ? `${l.email} · ` : ""}{l.source}
                    {l.next_follow_up ? ` · follow up ${l.next_follow_up}` : ""}
                    {l.notes ? ` · ${l.notes}` : ""}
                  </p>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  {l.ig_handle && (
                    <a href={`https://instagram.com/${l.ig_handle}`} target="_blank" rel="noreferrer" className="p-2 text-ink/50 hover:text-ink transition-colors" data-testid={`lead-ig-${l.id}`}>
                      <Instagram size={16} />
                    </a>
                  )}
                  <select value={l.stage} onChange={(e) => setStage(l, e.target.value)}
                    className={`overline px-2.5 py-1.5 bg-transparent cursor-pointer focus:outline-none ${STAGE_STYLE[l.stage] || ""}`}
                    data-testid={`lead-stage-${l.id}`}>
                    {STAGES.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
                  </select>
                  <button onClick={() => remove(l)} className="p-2 text-ink/35 hover:text-ink transition-colors" data-testid={`lead-delete-${l.id}`}>
                    <Trash2 size={16} />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {drawer && (
        <Drawer title={drawer.id ? "Edit lead" : "New lead"} onClose={() => setDrawer(null)}>
          <div className="space-y-5" data-testid="lead-form">
            <Field label="Name" v={drawer.name} on={(v) => setDrawer({ ...drawer, name: v })} testid="lead-name-input" />
            <Field label="Instagram handle (without @)" v={drawer.ig_handle} on={(v) => setDrawer({ ...drawer, ig_handle: v })} testid="lead-handle-input" />
            <Field label="Business" v={drawer.business_name} on={(v) => setDrawer({ ...drawer, business_name: v })} testid="lead-business-input" />
            <Select label="How they found us" v={drawer.source} on={(v) => setDrawer({ ...drawer, source: v })} options={SOURCES} testid="lead-source-select" />
            <Select label="Stage" v={drawer.stage} on={(v) => setDrawer({ ...drawer, stage: v })} options={STAGES} testid="lead-stage-select" />
            <label className="block">
              <span className="overline text-ink/50">Follow up on</span>
              <input type="date" value={drawer.next_follow_up || ""} onChange={(e) => setDrawer({ ...drawer, next_follow_up: e.target.value })}
                className="w-full bg-transparent border-b border-ink/30 py-2.5 mt-1.5 font-sans text-base focus:outline-none focus:border-gloss-silver transition-colors" data-testid="lead-followup-input" />
            </label>
            <Area label="Notes" v={drawer.notes} on={(v) => setDrawer({ ...drawer, notes: v })} rows={3} testid="lead-notes-input" />
            <button onClick={save} disabled={saving} className="overline bg-ink text-cream px-6 py-3.5 hover:bg-ink/85 transition-colors disabled:opacity-50 w-full" data-testid="lead-save-btn">
              {saving ? "Saving…" : drawer.id ? "Save changes" : "Add to the book"}
            </button>
          </div>
        </Drawer>
      )}
    </div>
  );
}

const FilterChip = ({ label, on, selected, testid }) => (
  <button onClick={on} className={`overline px-3.5 py-2 transition-colors ${selected ? "bg-ink text-cream" : "border border-ink/25 text-ink/60 hover:border-ink hover:text-ink"}`} data-testid={testid}>
    {label}
  </button>
);

const BriefRow = ({ l, note, onSnooze, onDone }) => (
  <div className="flex flex-col sm:flex-row sm:items-center gap-2 bg-white border border-ink/10 px-4 py-3" data-testid={`brief-row-${l.id}`}>
    <div className="min-w-0 flex-1">
      <p className="font-sans text-sm truncate"><span className="font-medium">{l.name || `@${l.ig_handle}`}</span>{l.business_name ? ` — ${l.business_name}` : ""}</p>
      <p className="font-sans text-xs text-ink/55">{note} · currently {STAGE_LABEL[l.stage]}</p>
    </div>
    <div className="flex items-center gap-2 shrink-0">
      {l.ig_handle && (
        <a href={`https://instagram.com/${l.ig_handle}`} target="_blank" rel="noreferrer" className="overline border border-ink px-3 py-1.5 hover:bg-ink hover:text-cream transition-colors flex items-center gap-1.5" data-testid={`brief-dm-${l.id}`}>
          <Instagram size={12} /> Open IG
        </a>
      )}
      <button onClick={onDone} className="overline bg-ink text-cream px-3 py-1.5 hover:bg-ink/85 transition-colors" data-testid={`brief-done-${l.id}`}>Touched base</button>
      <button onClick={onSnooze} className="overline border border-ink/30 px-3 py-1.5 text-ink/60 hover:border-ink hover:text-ink transition-colors flex items-center gap-1.5" data-testid={`brief-snooze-${l.id}`}>
        <Clock size={12} /> +2 days
      </button>
    </div>
  </div>
);
